import { useReducer } from "react";
import { useFilteredMattersQuery } from "./matters";
import { ClientValue } from "@design-system";
import { FilteredMattersInput } from "~/gql/graphql";
const initialState: {
  [key: string]: any;
  clientFilter?: number;
  practiceAreaFilter?: number;
  titleFilter?: string | null;
  page: number;
} = {
  matterCount: null,
  matters: [],
  page: 1,
  titleFilter: null,
  practiceAreaFilter: undefined,
  clientFilter: undefined,
  pageSize: 10,
};

export const Action = {
  PracticeAreaFilterChanged: "PRACTICE_AREA_FILTER_CHANGED",
  TitleFilterChanged: "TITLE_FILTER_CHANGED",
  ClientFilterChanged: "CLIENT_FILTER_CHANGED",
  PageChanged: "PAGE_CHANGED",
};

function reducer(state: any, action: { type: string; value?: any }) {
  switch (action.type) {
    case Action.PracticeAreaFilterChanged:
      return {
        ...state,
        page: 1,
        practiceAreaFilter: action.value,
      };
    case Action.ClientFilterChanged:
      return {
        ...state,
        page: 1,
        clientFilter: action.value,
      };

    case Action.TitleFilterChanged:
      return {
        ...state,
        page: 1,
        titleFilter: action.value,
      };

    case Action.PageChanged:
      return { ...state, page: action.value.page, pageSize: action.value.pageSize };
    default:
      throw new Error(`Unknown action, ${action.type}`);
  }
}

export const LibraryMatterTypeObj = {
  TEMPLATE: "TEMPLATE",
  HISTORICAL: "HISTORICAL",
  FAVOURITE: "FAVOURITE",
  RECENT: "RECENT",
};

export type LibraryMatterType = typeof LibraryMatterTypeObj[keyof typeof LibraryMatterTypeObj];

export const usePaginatedMatterQuery = (
  libraryMatterType: LibraryMatterType,
  defaultState?: Object,
  excludeMatter?: number
) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...defaultState });

  const template = libraryMatterType === LibraryMatterTypeObj.TEMPLATE;

  const filter: FilteredMattersInput = {
    filter: {
      template,
      ...(libraryMatterType === LibraryMatterTypeObj.FAVOURITE && { favourite: true }),
      ...(libraryMatterType === LibraryMatterTypeObj.RECENT && { recent: true }),
      ...(state.clientFilter && { clientId: state.clientFilter }),
      ...(state.practiceAreaFilter && {
        organisationalUnitId: state.practiceAreaFilter,
      }),
      ...(excludeMatter && { excludeMatterId: excludeMatter }),
      ...(state.titleFilter && {
        title: state.titleFilter,
      }),
    },
  };

  const { data, loading } = useFilteredMattersQuery({
    ...filter,
    offset: (state.page - 1) * state.pageSize,
    limit: state.pageSize,
    orderBy: [{ title: "asc" }, { updatedAt: "desc" }],
  });

  const onChangeClient = (client: ClientValue) => {
    dispatch({ type: Action.ClientFilterChanged, value: client.id });
  };

  const onChangePracticeArea = (practiceAreaId: number) => {
    dispatch({ type: Action.PracticeAreaFilterChanged, value: practiceAreaId });
  };

  const onChangeTitleSearch = (title: string) => {
    dispatch({ type: Action.TitleFilterChanged, value: title });
  };

  const onChangePage = (page: number, pageSize: number) => {
    dispatch({ type: Action.PageChanged, value: { page, pageSize } });
  };

  return {
    count: data?.filteredMatters?.count,
    matters: data?.filteredMatters?.matters,
    page: state.page,
    pageSize: state.pageSize,
    loading,
    onChangeClient,
    onChangeTitleSearch,
    onChangePracticeArea,
    onChangePage,
  };
};
